import { apiClient } from './apiClient';

// Types
export interface User {
  id: string;
  email: string;
  name: string;
  role: 'user' | 'creator' | 'partner' | 'admin' | 'god';
  status: 'active' | 'inactive' | 'suspended';
  email_verified: boolean;
  avatar_url: string | null;
  created_at: string;
  updated_at: string;
  last_login_at: string | null;
  deleted_at: string | null;
  plan_id: string | null;
  plan_name: string | null;
  plan_slug: string | null;
  is_test_user?: boolean;

  // Campos de perfil — só vêm no GET /auth/users/:id
  username?: string | null;
  cpf?: string | null;
  phone?: string | null;
  city?: string | null;
  state?: string | null;
  profession?: string | null;
  profile_type?: string | null;
  how_knew?: string | null;
  ai_familiarity?: string | null;
  bio?: string | null;
  observations?: string | null;
  property_size_ha?: string | number | null;
  biome?: string | null;
  soil_type?: string | null;
  property_city?: string | null;
  property_state?: string | null;
  production_system?: string | null;
  gaas_associated?: string | null;
  activities?: string[] | null;
  practices?: string[] | null;
  challenges?: string[] | null;
  main_objective?: string | null;
  professional_formation?: string | null;
  work_area?: string | null;
  work_region?: string | null;
  work_type?: string | null;
  regenerative_experience?: string | null;
  experience_years?: string | null;
  institution?: string | null;
  work_field?: string | null;
  main_interest?: string | null;
  preferred_topics?: string[] | null;
  terms_accepted?: boolean | null;
  terms_accepted_at?: string | null;
  data_sharing_consent?: boolean | null;
}

export interface CreateUserInput {
  // Obrigatórios
  email: string;
  password: string;
  name: string;

  role?: 'user' | 'creator' | 'partner' | 'admin' | 'god';
  planId?: string;
  isTestUser?: boolean;

  // Identificação extra (CPF e username opcionais)
  username?: string;
  cpf?: string;
  phone?: string;

  // Localização e perfil declarado
  city?: string;
  state?: string;
  profession?: string;
  profileType?: string;
  howKnew?: string;
  aiFamiliarity?: string;

  // Questionário — produtor rural
  propertySizeHa?: number;
  biome?: string;
  soilType?: string;
  propertyCity?: string;
  propertyState?: string;
  productionSystem?: string;
  gaasAssociated?: string;
  activities?: string[];
  practices?: string[];
  challenges?: string[];
  mainObjective?: string;

  // Questionário — técnico/consultor
  professionalFormation?: string;
  workArea?: string;
  workRegion?: string;
  workType?: string;
  regenerativeExperience?: string;
  experienceYears?: string;

  // Questionário — pesquisador/estudante/instituição
  institution?: string;
  workField?: string;
  mainInterest?: string;
  preferredTopics?: string[];

  // Preferências e aceite
  bio?: string;
  observations?: string;
  dataSharingConsent?: boolean;
  termsAccepted?: boolean;
}

export interface UpdateUserInput {
  name?: string;
  role?: 'user' | 'creator' | 'partner' | 'admin' | 'god';
  status?: 'active' | 'inactive' | 'suspended';
  avatar_url?: string | null;
}

export interface ListUsersParams {
  page?: number;
  limit?: number;
  search?: string;
  role?: string;
  status?: string;
  includeDeleted?: boolean;
  isTestUser?: boolean;
}

export interface ListUsersResponse {
  users: User[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Users Service
class UsersService {
  /**
   * Listar usuários com paginação e filtros
   */
  async listUsers(params?: ListUsersParams): Promise<ListUsersResponse> {
    const response = await apiClient.get<ListUsersResponse>('/auth/users', { params });
    return response.data;
  }

  /**
   * Obter usuário por ID
   */
  async getUserById(id: string): Promise<User> {
    const response = await apiClient.get<{ user: User }>(`/auth/users/${id}`);
    return response.data.user;
  }

  /**
   * Criar novo usuário
   */
  async createUser(data: CreateUserInput): Promise<User> {
    const response = await apiClient.post<{ user: User }>('/auth/users', data);
    return response.data.user;
  }

  /**
   * Atualizar usuário
   */
  async updateUser(id: string, data: UpdateUserInput): Promise<User> {
    const response = await apiClient.put<{ user: User }>(`/auth/users/${id}`, data);
    return response.data.user;
  }

  /**
   * Deletar usuário
   */
  async deleteUser(id: string): Promise<void> {
    await apiClient.delete(`/auth/users/${id}`);
  }

  /**
   * Restaurar usuário deletado
   */
  async restoreUser(id: string): Promise<void> {
    await apiClient.post(`/auth/users/${id}/restore`);
  }
}

export default new UsersService();
